import { useState } from 'react';
import { Button, Text } from '@querodelivery/uni-react';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import { formatCurrency } from '@/Utils/FnUtils';
import { AdjustPostPaidLimitModal } from '@/_features/post-paid/subfeatures/adjust-post-paid-limit-modal/adjust-post-paid-limit-modal';
import type { PostPaidStatus, Place } from '@/_types/places';
import styles from './post-paid-section.module.scss';
import { useAppSelector } from '@/_store/hooks';
import { selectUser } from '@/_store/modules/auth/selectors';
import { ROLE_TYPES } from '@/Interfaces/Data';

const POST_PAID_STATUS_LABEL: Record<PostPaidStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  FROZEN: 'Congelado',
  OPEN_BALANCE_REPORT: 'Fatura a pagar',
};

interface PostPaidSectionProps {
  placeData?: Place;
  isLoading: boolean;
  placeId: string;
}

export const PostPaidSection = ({
  placeData,
  isLoading,
  placeId,
}: PostPaidSectionProps) => {
  const [isAdjustLimitModalOpen, setIsAdjustLimitModalOpen] = useState(false);

  const isPostPaidActive = placeData?.postPaid?.statusPostPaid === 'ACTIVE';

  const user = useAppSelector(selectUser);

  const isAdmin = user?.managerLogType === ROLE_TYPES.ADMIN;

  const isUsedPostPaidExceedingLimit =
    placeData &&
    placeData.postPaid &&
    placeData.maxLimitAllowed !== undefined &&
    placeData.postPaid.usedPostPaid >= placeData.maxLimitAllowed;

  const canShowAdjustLimitButton =
    isPostPaidActive && !isUsedPostPaidExceedingLimit && !!isAdmin;

  const availableLimit = placeData?.postPaid?.postPaidLimit
    ? placeData.postPaid.postPaidLimit - (placeData.postPaid.usedPostPaid || 0)
    : 0;

  const usedValue = placeData?.postPaid?.usedPostPaid || 0;

  if (isLoading) {
    return (
      <div className={styles.boxContainer}>
        <Skeleton width={80} height={20} borderRadius={8} />
        <div className={styles.postPaidRow}>
          <Skeleton width={80} height={20} borderRadius={8} />
          <Skeleton width={80} height={20} borderRadius={8} />
        </div>
        <div className={styles.postPaidRow}>
          <Skeleton width={100} height={20} borderRadius={8} />
          <Skeleton width={80} height={20} borderRadius={8} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.boxContainer}>
        <div className={styles.postPaidRow}>
          <Text tone="secondary" fontWeight="semibold" variant="bodyMd">
            Pós pago
          </Text>
          <div
            className={`${styles.postPaidStatusBadge} ${
              styles[
                placeData?.postPaid?.statusPostPaid
                  ? `badge--${placeData?.postPaid?.statusPostPaid?.toLowerCase()}`
                  : 'badge--inactive'
              ]
            }`}
          >
            <Text fontWeight="medium" variant="bodyMd">
              {
                POST_PAID_STATUS_LABEL[
                  placeData?.postPaid?.statusPostPaid || 'INACTIVE'
                ]
              }
            </Text>
          </div>
        </div>
        <div className={styles.postPaidRow}>
          <Text tone="secondary" fontWeight="medium" variant="bodyMd">
            Limite total:
          </Text>
          <Text tone="base" fontWeight="medium" variant="bodyMd">
            {formatCurrency(placeData?.postPaid?.postPaidLimit || 0)}
          </Text>
        </div>
        <div className={styles.postPaidRow}>
          <Text tone="secondary" fontWeight="medium" variant="bodyMd">
            Valor utilizado:
          </Text>
          <Text tone="error" fontWeight="medium" variant="bodyMd">
            {formatCurrency(usedValue)}
          </Text>
        </div>
        <div className={styles.postPaidRow}>
          <Text tone="secondary" fontWeight="medium" variant="bodyMd">
            Valor disponível:
          </Text>
          <Text tone="success" fontWeight="medium" variant="bodyMd">
            {formatCurrency(availableLimit)}
          </Text>
        </div>
        {canShowAdjustLimitButton && (
          <div className={styles.buttonContainer}>
            <Button
              size="lg"
              width="40%"
              onClick={() => setIsAdjustLimitModalOpen(true)}
            >
              ajustar limite
            </Button>
          </div>
        )}
      </div>

      <AdjustPostPaidLimitModal
        open={isAdjustLimitModalOpen}
        onClose={() => setIsAdjustLimitModalOpen(false)}
        placeId={placeId}
      />
    </>
  );
};
